import { ReactNode, useEffect, useState } from 'react';

/**
 * toast('已保存 / saved', { kind: 'success' }) from anywhere; <Toaster/> renders them.
 * Module-level emitter so call sites need no context. Mounted once in ChromeLayout and once
 * in the PublicBidPage branch (which lives outside ChromeLayout).
 */
type Kind = 'success' | 'error' | 'info';
type Item = { id: number; msg: ReactNode; kind: Kind; leaving?: boolean };

let seq = 0;
let listeners: ((t: Omit<Item, 'leaving'> & { duration: number }) => void)[] = [];

export function toast(msg: ReactNode, opts?: { kind?: Kind; duration?: number }) {
  const t = { id: ++seq, msg, kind: opts?.kind ?? 'info', duration: opts?.duration ?? 3200 };
  if (listeners.length === 0) console.warn('toast() fired with no <Toaster/> mounted', msg);
  listeners.forEach((l) => l(t));
}

export function Toaster() {
  const [items, setItems] = useState<Item[]>([]);
  useEffect(() => {
    const onToast = (t: Omit<Item, 'leaving'> & { duration: number }) => {
      setItems((cur) => [...cur, t]);
      // fade out, then unmount after the transition
      window.setTimeout(() => setItems((cur) => cur.map((i) => (i.id === t.id ? { ...i, leaving: true } : i))), t.duration);
      window.setTimeout(() => setItems((cur) => cur.filter((i) => i.id !== t.id)), t.duration + 400);
    };
    listeners.push(onToast);
    return () => { listeners = listeners.filter((l) => l !== onToast); };
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="toaster" role="status" aria-live="polite">
      {items.map((i) => (
        <div key={i.id} className={`toast${i.kind !== 'info' ? ` toast-${i.kind}` : ''}${i.leaving ? ' leaving' : ''}`}>
          {i.kind === 'success' ? '✓ ' : i.kind === 'error' ? '⚠ ' : ''}{i.msg}
        </div>
      ))}
    </div>
  );
}
